import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { DfpSourceDocumentPreviewService } from '@src/app/features/dashboard/performance/pages/dfp/services/dfp-source-document-preview.service';
import {
  DfpSourceExclusionReason,
  DfpStatementKind,
  dfpSourceDocumentCoverage,
  dfpSourceDocumentResponse,
  dfpSourceDocumentsResponse,
} from '@interfaces/dfp/response/dfpSourceDocumentsResponse';
import { SharedModule } from '@shared/shared.module';
import { AuditSealComponent } from '@shared/components/audit-seal/audit-seal.component';
import { isoDateToBr } from '@shared/utils/financial-statement-audit.util';
import { DfpPanelComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/dfp-panel/dfp-panel.component';
import { DfpPrecedenceInfoComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/precedence-info/precedence-info.component';
import {
  coveragePeriodLabel,
  sourceReplacedReason,
  sourceUsageSummary,
} from '@src/app/features/dashboard/performance/pages/dfp/utils/dfp-source-selection.util';

/**
 * Painel "Documentos de origem": lista os arquivos que alimentam as colunas exibidas e, separadamente, os que
 * ficaram de fora com o motivo.
 *
 * O segundo grupo é o ponto: é ele que responde "subi 4 documentos e a tela mostra 3". Não esconda a lista de
 * não utilizados quando estiver vazia sem deixar claro que a checagem foi feita — o silêncio é indistinguível
 * de "não perguntamos".
 *
 * Não busca dado: quem carrega e filtra é o `dfp.component`, então o painel não precisa saber de empresa, de
 * período pedido nem de ano oculto. A única ação que ele executa é abrir a modal compartilhada de preview
 * (`FilePreviewModalComponent`, a mesma do Data Room e do Onboarding v2) ao clicar no nome do arquivo.
 */
@Component({
  selector: 'app-source-documents-panel',
  standalone: true,
  imports: [
    DatePipe,
    SharedModule,
    DfpPanelComponent,
    AuditSealComponent,
    DfpPrecedenceInfoComponent,
  ],
  templateUrl: './source-documents-panel.component.html',
  styleUrls: ['./source-documents-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SourceDocumentsPanelComponent {
  private readonly preview = inject(DfpSourceDocumentPreviewService);

  @Input() documents: dfpSourceDocumentsResponse | null = null;

  /**
   * Pedido de remoção de um documento de origem (card #2932).
   *
   * O painel **não remove**: ele avisa. Quem confirma com o usuário, chama a API e recarrega a tela é o
   * `dfp.component`, pelo mesmo motivo que já vale para o carregamento — este componente não conhece
   * empresa nem período, e passar a conhecer só para remover o tornaria dependente de estado que ele hoje
   * não tem.
   */
  @Output() removeRequested = new EventEmitter<dfpSourceDocumentResponse>();

  /**
   * Se a ação de remover aparece para este documento. **Quem decide é o backend** (`canRemove`), não o
   * painel — mesma regra do `canSeeNotUsed`: permissão recalculada no cliente cria duas fontes de verdade.
   *
   * Vem `false` para usuário sem perfil de backoffice e para âncora sintética do De/Para automático, que
   * não é arquivo — o dado por trás dela se remove pela Padronização de dados contábeis. O painel não teria
   * como distinguir o segundo caso sozinho: o backend sobrescreve o nome da âncora pelo nome do arquivo de
   * origem antes de responder.
   */
  canRemove(document: dfpSourceDocumentResponse): boolean {
    return document.canRemove === true;
  }

  /** Emite o pedido; a confirmação é responsabilidade do componente pai. */
  requestRemove(document: dfpSourceDocumentResponse): void {
    this.removeRequested.emit(document);
  }

  /** Tipo selecionado no filtro; `null` é "Todos". */
  kindFilter: DfpStatementKind | null = null;

  /** Ordem dos filtros, igual à ordem das tabelas na tela (DRE, Balanço, Faturamento). */
  readonly kindOrder: DfpStatementKind[] = [
    DfpStatementKind.IncomeStatement,
    DfpStatementKind.BalanceSheet,
    DfpStatementKind.RevenueStatement,
  ];

  readonly kindLabels: Record<DfpStatementKind, string> = {
    [DfpStatementKind.IncomeStatement]: 'DRE',
    [DfpStatementKind.BalanceSheet]: 'Balanço',
    [DfpStatementKind.RevenueStatement]: 'Faturamento',
  };

  readonly kindIcons: Record<DfpStatementKind, string> = {
    [DfpStatementKind.IncomeStatement]: 'pi pi-chart-line',
    [DfpStatementKind.BalanceSheet]: 'pi pi-book',
    [DfpStatementKind.RevenueStatement]: 'pi pi-dollar',
  };

  /**
   * Nome exibido, clicabilidade e abertura vivem em `DfpSourceDocumentPreviewService`, não aqui: a seção
   * "Esta coluna vem de" do menu de coluna de ano usa exatamente as mesmas regras, e duas cópias divergiriam.
   * Os métodos abaixo são delegações finas mantidas para o template não precisar conhecer o serviço.
   */
  displayName(document: dfpSourceDocumentResponse): string {
    return this.preview.displayName(document);
  }

  /** `true` quando o título exibido é derivado — o template mostra o identificador real como apoio. */
  isSynthetic(document: dfpSourceDocumentResponse): boolean {
    return this.preview.isSynthetic(document);
  }

  canPreview(document: dfpSourceDocumentResponse): boolean {
    return this.preview.canPreview(document);
  }

  /** Motivo, no tooltip, de um nome não abrir — senão o usuário fica clicando sem entender. */
  previewHint(document: dfpSourceDocumentResponse): string {
    return this.preview.previewHint(document);
  }

  /** Abre na modal compartilhada de preview — ver `DfpSourceDocumentPreviewService.open`. */
  openPreview(document: dfpSourceDocumentResponse): void {
    this.preview.open(document);
  }

  /** Documentos em uso já filtrados pelo tipo selecionado. */
  get inUse(): dfpSourceDocumentResponse[] {
    return this.applyKindFilter(this.documents?.inUse);
  }

  /** Documentos não utilizados já filtrados pelo tipo selecionado. */
  get notUsed(): dfpSourceDocumentResponse[] {
    return this.applyKindFilter(this.documents?.notUsed);
  }

  /**
   * A coluna "Não utilizados" é restrita a ADMIN e TENANT, e quem decide é o BACKEND: ele devolve `notUsed`
   * como `null` para os outros perfis. O painel não checa role nenhuma — se checasse, haveria duas fontes de
   * verdade para a mesma regra, e a do cliente seria a errada (os documentos já teriam vindo na resposta).
   *
   * Compara com `null`/`undefined` explicitamente, e não por truthiness: lista **vazia** é um estado legítimo
   * de quem TEM acesso, e a coluna precisa aparecer para dizer "nenhum documento ficou de fora".
   */
  get canSeeNotUsed(): boolean {
    return this.documents?.notUsed !== null && this.documents?.notUsed !== undefined;
  }

  /**
   * Total do tipo somando os dois grupos — é o número que aparece no chip do filtro.
   *
   * Para quem não vê "Não utilizados", `notUsed` é `null` e o `?? []` faz a contagem cair só sobre "Em uso" —
   * de propósito: um chip "Todos 30" acima de uma única coluna com 5 linhas não só desalinharia visualmente,
   * como vazaria em número exatamente a informação que a coluna oculta protege.
   */
  totalFor(kind: DfpStatementKind | null): number {
    const todos = [...(this.documents?.inUse ?? []), ...(this.documents?.notUsed ?? [])];
    return kind === null ? todos.length : todos.filter(document => document.kind === kind).length;
  }

  /**
   * O chip só aparece quando existe documento daquele tipo. Um filtro que não filtra nada é ruído, e
   * pior: sugere que a empresa deveria ter aquele demonstrativo quando ela simplesmente não tem.
   */
  hasKind(kind: DfpStatementKind): boolean {
    return this.totalFor(kind) > 0;
  }

  setKindFilter(kind: DfpStatementKind | null): void {
    this.kindFilter = kind;
  }

  /**
   * Texto do estado vazio de cada coluna. Muda com o filtro de propósito: com filtro ativo, "nenhum
   * documento" sozinho faria o usuário concluir que não há nada, quando na verdade há — só não daquele
   * tipo. A coluna nunca fica muda; ver a regra de estado vazio honesto no topo da classe.
   */
  emptyLabel(grupo: 'inUse' | 'notUsed'): string {
    const tipo = this.kindFilter === null ? '' : ` de ${this.kindLabels[this.kindFilter]}`;

    return grupo === 'inUse'
      ? `Nenhum documento${tipo} identificado como origem das colunas exibidas.`
      : `Nenhum documento${tipo} ficou de fora — todos alimentam as colunas exibidas.`;
  }

  private applyKindFilter(
    lista: dfpSourceDocumentResponse[] | null | undefined
  ): dfpSourceDocumentResponse[] {
    const documentos = lista ?? [];
    return this.kindFilter === null
      ? documentos
      : documentos.filter(document => document.kind === this.kindFilter);
  }

  /**
   * Nome de quem enviou, para o rodapé da linha. `UserDto` não tem campo `name`: o nome é
   * `firstName` + `surName`, e o e-mail é o fallback quando o nome não veio. String vazia quando
   * não há usuário — o template omite o trecho "por …" nesse caso.
   */
  uploaderName(document: dfpSourceDocumentResponse): string {
    const user = document.createdByUser;
    if (!user) return '';

    const fullName = [user.firstName, user.surName].filter(Boolean).join(' ').trim();

    return fullName || user.email || '';
  }

  /**
   * Rótulo do que o documento cobre: "2024 · jan–jun, 2023" — mesmo formato de período do resto da tela.
   *
   * Faturamento é colapsado por ANO antes de virar rótulo: lá a cobertura é por MÊS (o backend carimba
   * `periodStartMonth === periodEndMonth === mês` em cada linha de faturamento, ver
   * `DfpSourceDocumentService.BuildRevenueStatementDocuments`), então um arquivo normal de 12 meses renderizaria
   * "2024 · jan, 2024 · fev, … 2024 · dez" e estouraria a linha de meta do painel, que não tem reticências. O
   * dado por mês está correto e é útil ao menu de ano — o colapso é só de APRESENTAÇÃO, aqui: um item por ano,
   * com o intervalo entre o menor e o maior mês daquele ano ("2024 · jan–dez").
   */
  coverageLabel(document: dfpSourceDocumentResponse): string {
    const coverage =
      document.kind === DfpStatementKind.RevenueStatement
        ? this.collapseCoverageByYear(document)
        : document.coverage;

    return coverage
      .map(item => {
        // Texto do backend, senão "período não identificado" ou os meses (ver `coveragePeriodLabel`).
        const period = coveragePeriodLabel(item);
        return period ? `${item.year} · ${period}` : `${item.year}`;
      })
      .join(', ');
  }

  /**
   * Uma entrada por ano, com o menor mês inicial e o maior mês final daquele ano. Mês nulo (linha anual, sem
   * carimbo de mês) mantém o ano sem período — o rótulo vira só "2024".
   */
  private collapseCoverageByYear(document: dfpSourceDocumentResponse): dfpSourceDocumentCoverage[] {
    const byYear = new Map<number, dfpSourceDocumentCoverage>();

    for (const item of document.coverage) {
      const current = byYear.get(item.year);
      if (!current) {
        byYear.set(item.year, { ...item });
        continue;
      }

      byYear.set(item.year, {
        year: item.year,
        periodStartMonth: this.minMonth(current.periodStartMonth, item.periodStartMonth),
        periodEndMonth: this.maxMonth(current.periodEndMonth, item.periodEndMonth),
        rowCount: current.rowCount + item.rowCount,
        periodUnknown: !!current.periodUnknown && !!item.periodUnknown,
      });
    }

    return Array.from(byYear.values()).sort((a, b) => a.year - b.year);
  }

  private minMonth(a: number | null, b: number | null): number | null {
    if (a === null || b === null) return null;
    return Math.min(a, b);
  }

  private maxMonth(a: number | null, b: number | null): number | null {
    if (a === null || b === null) return null;
    return Math.max(a, b);
  }

  /**
   * Por que um documento EM USO vale ("Auditado (firma X) tem preferência sobre não auditado", "Cobre mais meses"),
   * por ano quando o motivo varia. Escrito pelo backend, que é quem aplica a regra; vazio quando não veio.
   */
  usageReason(document: dfpSourceDocumentResponse): string {
    return sourceUsageSummary(document);
  }

  /** Por que um documento NÃO utilizado foi substituído (`replacedReason`); vazio quando não veio. */
  replacedReason(document: dfpSourceDocumentResponse): string {
    return sourceReplacedReason(document);
  }

  /** Frase do motivo, já com a data quando ela existe. */
  reasonLabel(document: dfpSourceDocumentResponse): string {
    switch (document.reason) {
      case DfpSourceExclusionReason.ProcessingFailed:
        return 'Falha no processamento — o arquivo foi enviado, mas a extração não concluiu';
      case DfpSourceExclusionReason.NoExtractedData:
        return 'Nenhum dado foi extraído deste arquivo';
      case DfpSourceExclusionReason.ReplacedByResubmission:
        // Data por texto (`isoDateToBr`), sem `new Date`: a conversão para o fuso do navegador mostrava o dia
        // anterior para quem está em GMT-3 (12/08 00:00 UTC virava 11/08), e a régua de datas do projeto proíbe
        // `new Date` fora do `DateFormatterService`.
        return document.replacedAt && isoDateToBr(document.replacedAt)
          ? `Substituído por um reenvio em ${isoDateToBr(document.replacedAt)}`
          : 'Substituído por um reenvio mais recente';
      case DfpSourceExclusionReason.PeriodNotDisplayed:
        return 'Período não exibido nas colunas atuais';
      default:
        return '';
    }
  }
}
